import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Loader2, X, ExternalLink } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';

// PDF preview drawn page by page to fit the width. Used on phones and in the iOS app, where a PDF in an
// iframe renders at full size and pans sideways. Uses pdf.js's legacy build so older iOS Safari works too.
export function PdfPages({ url, label }: { url: string; label: string }) {
  const c = useAppColors();
  const box = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  useEffect(() => {
    let off = false;
    const host = box.current;
    if (!host) return;
    host.innerHTML = '';
    setState('loading');
    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
        const pdf = await pdfjs.getDocument({ url }).promise;
        const width = host.clientWidth || 360;
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        for (let i = 1; i <= Math.min(pdf.numPages, 30); i++) {
          if (off) return;
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const vp = page.getViewport({ scale: (width / base.width) * dpr });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(vp.width); canvas.height = Math.floor(vp.height);
          canvas.style.width = '100%'; canvas.style.height = 'auto'; canvas.style.display = 'block';
          canvas.style.background = '#fff'; canvas.style.borderRadius = '10px';
          canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', `${label}, page ${i}`);
          host.appendChild(canvas);
          await page.render({ canvas, canvasContext: canvas.getContext('2d')!, viewport: vp } as never).promise;
          if (i === 1 && !off) setState('ready');
        }
      } catch (e) {
        console.warn('[pdf preview]', e);
        if (!off) setState('failed');
      }
    })();
    return () => { off = true; };
  }, [url, label]);
  return (
    <div className="w-full">
      {state === 'loading' && <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>}
      {state === 'failed' && (
        <div className="text-center p-8 space-y-3">
          <p className="text-sm" style={{ color: c.textDim }}>The preview couldn't load.</p>
          <a href={url} target="_blank" rel="noopener" className="inline-flex h-10 px-4 rounded-xl text-sm font-semibold items-center gap-2" style={{ background: c.accent, color: '#fff' }}><Download size={15} /> Open the PDF</a>
        </div>
      )}
      <div ref={box} className="flex flex-col gap-3" />
    </div>
  );
}


// Full-screen viewer for PDFs and photos, opened with
//   window.dispatchEvent(new CustomEvent('opsalo:view-file', { detail: { url, title } }))
// Used instead of window.open, which does nothing in the iOS app (and after an await, gets blocked in browsers).
export function viewFile(url: string, title: string) {
  window.dispatchEvent(new CustomEvent('opsalo:view-file', { detail: { url, title } }));
}

export function FileViewerHost() {
  const c = useAppColors();
  const [f, setF] = useState<{ url: string; title: string } | null>(null);
  useEffect(() => {
    const on = (e: Event) => setF((e as CustomEvent<{ url: string; title: string }>).detail);
    window.addEventListener('opsalo:view-file', on);
    return () => window.removeEventListener('opsalo:view-file', on);
  }, []);
  useEffect(() => {
    if (!f) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setF(null); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [f]);
  if (!f) return null;
  const path = f.url.split('?')[0].toLowerCase();
  const isPdf = path.endsWith('.pdf');
  const isImg = /\.(png|jpe?g|webp|gif)$/.test(path);
  const root = document.documentElement.classList.contains('cap-native') ? document.getElementById('root') ?? document.body : document.body;
  return createPortal(
    <div className="fixed inset-0 z-[400] flex flex-col" style={{ background: c.bg }} role="dialog" aria-label={f.title}>
      <header className="flex items-center gap-2 px-3 py-2" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)', borderBottom: `1px solid ${c.border}` }}>
        <button onClick={() => setF(null)} aria-label="Close" className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ color: c.text }}><X size={20} /></button>
        <h2 className="flex-1 min-w-0 truncate text-[15px] font-bold" style={{ color: c.text }}>{f.title}</h2>
        <a href={f.url} target="_blank" rel="noopener" className="h-10 px-3 rounded-xl text-[13px] font-semibold flex items-center gap-1.5" style={{ background: c.iconBg, color: c.text }}>
          <ExternalLink size={15} /> Open
        </a>
      </header>
      <div className="flex-1 overflow-y-auto p-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}>
        <div className="max-w-3xl mx-auto">
          {isPdf ? <PdfPages url={f.url} label={f.title} />
            : isImg ? <img src={f.url} alt={f.title} className="w-full h-auto rounded-xl" />
            : <p className="text-sm text-center py-16" style={{ color: c.textDim }}>This file can't be shown here. Tap Open.</p>}
        </div>
      </div>
    </div>,
    root,
  );
}
